import { useEffect } from 'react';

const DEFAULT_OG_IMAGE = 'https://stagecrew.app/stagecrew-share-2026-07.png';
const DEFAULT_OG_IMAGE_ALT = 'StageCrew — explore sourced AV and stagehand listings and live-event profiles, review the details, and decide on your terms.';

interface SEOHeadProps {
  title: string;
  description: string;
  url?: string;
  canonical?: string | false;
  type?: string;
  image?: string;
  imageAlt?: string;
  keywords?: string;
  jsonLd?: Record<string, any>;
  robots?: string;
}

/**
 * Manages document.title, meta description, OG tags, and JSON-LD structured data.
 * Uses direct DOM manipulation — works without react-helmet dependency.
 */
const SEOHead: React.FC<SEOHeadProps> = ({ title, description, url, canonical, type = 'website', image, imageAlt, keywords, jsonLd, robots }) => {
  useEffect(() => {
    // Set document title
    document.title = title;

    // Helper to set/create meta tags
    const setMeta = (attr: string, key: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('name', 'description', description);
    if (keywords) setMeta('name', 'keywords', keywords);
    if (robots) {
      setMeta('name', 'robots', robots);
    } else {
      const robotsEl = document.querySelector('meta[name="robots"]');
      if (robotsEl) robotsEl.remove();
    }
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:type', type);
    setMeta('property', 'og:site_name', 'StageCrew');
    if (url) setMeta('property', 'og:url', url);
    const ogImage = image || DEFAULT_OG_IMAGE;
    const ogImageAlt = imageAlt || DEFAULT_OG_IMAGE_ALT;
    setMeta('property', 'og:image', ogImage);
    setMeta('property', 'og:image:type', 'image/png');
    setMeta('property', 'og:image:width', '1200');
    setMeta('property', 'og:image:height', '630');
    setMeta('property', 'og:image:alt', ogImageAlt);
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', ogImage);
    setMeta('name', 'twitter:image:alt', ogImageAlt);

    // Canonical link
    const canonicalUrl = canonical === false ? undefined : (canonical || url);
    let linkEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (canonicalUrl) {
      if (!linkEl) {
        linkEl = document.createElement('link');
        linkEl.setAttribute('rel', 'canonical');
        document.head.appendChild(linkEl);
      }
      linkEl.setAttribute('href', canonicalUrl);
    } else if (linkEl) {
      linkEl.remove();
    }

    // The static shell includes homepage JSON-LD for first paint. Once React
    // owns the page, remove that bootstrap data so it cannot describe a later
    // client-side route. Pages with structured data add their route-specific
    // payload below.
    const bootstrapJsonLd = document.getElementById('seo-bootstrap-jsonld');
    if (bootstrapJsonLd) bootstrapJsonLd.remove();

    // JSON-LD structured data
    let scriptEl = document.getElementById('seo-jsonld') as HTMLScriptElement | null;
    if (jsonLd) {
      if (!scriptEl) {
        scriptEl = document.createElement('script');
        scriptEl.id = 'seo-jsonld';
        scriptEl.type = 'application/ld+json';
        document.head.appendChild(scriptEl);
      }
      scriptEl.textContent = JSON.stringify(jsonLd);
    } else if (scriptEl) {
      scriptEl.remove();
    }

    // Cleanup on unmount
    return () => {
      const jsonLdEl = document.getElementById('seo-jsonld');
      if (jsonLdEl) jsonLdEl.remove();
      const canonicalEl = document.querySelector('link[rel="canonical"]');
      if (canonicalEl) canonicalEl.remove();
    };
  }, [title, description, url, canonical, type, image, imageAlt, keywords, jsonLd, robots]);

  return null;
};

export default SEOHead;
